/**
 * The address of /strategic-kpis (masterplan skpi-redesign, §4 «URL», §2 rule 11; package A2a).
 *
 * Pure functions only: they read and write `URLSearchParams` and know nothing about React, the router
 * or the payload. The page (A2b) calls `parseSkpiUrl` on every render, writes with `withSkpiState`
 * (`replace: true`) and opens the drill view with `withDrill` (the one change that pushes a history
 * entry, so the browser's Back leaves the rows again).
 *
 * Params owned here, each written only when it differs from its default:
 *   tab      dashboard | people | targets | quality | definitions          (default dashboard)
 *   People   view ranking|matrix (ranking) · kpi · group none|team|company (none) · fam · cell (achievement)
 *            · hier company-team|team (company-team; the matrix hierarchy, kept from K5)
 *   Targets  level person|team|company|bu (person) · fam
 *   Drill    rows=1 + dkpi · dscope · dkey (the node whose rows are shown)
 * Not owned here and passed through untouched: bu, year, companies, teams, persons (useStrategicKpis).
 *
 * Old links (rule 11): `?tab=dashboard|targets|definitions` stay as they are; `?view=leaderboard|matrix`
 * was the drill-down on the dashboard and now lands on `tab=people` (leaderboard → ranking); so does an
 * old dashboard link that carries only `kpi`/`group` (ranking) or `cell`/`hier` (matrix).
 * `normalizeSkpiParams` rewrites such an address once so the bar shows the canonical form.
 *
 * People state across tabs (E22, Roman 2026-10-19): view, kpi, group, cell and hier survive a tab change,
 * as they did when the old page's setTab touched only `tab`. The address keeps its canonical form (a
 * tab's params only on that tab, so old links stay unambiguous); the page remembers the People state
 * it leaves (`peopleStateOf`) and `withTab(…, 'people', { people })` puts it back. The last chosen view
 * is also a per-person preference in localStorage `strategic-kpis-drill-view` (as before the redesign):
 * it fills a People address that names no view (`SkpiUrlOptions.storedView`); a link that names one wins.
 *
 * Old browser storage: `SKPI_STORAGE_KEYS` is the inventory of every `strategic-kpis-*` localStorage key
 * the page and its components wrote before the redesign (grep 2026-10-19, including the six
 * `*-col-widths` of useResizableColumns), each with the phase whose package retires its owner.
 * `clearRetiredSkpiStorage(phase)` deletes the keys retired up to that phase; a key whose owner is still
 * on screen is never deleted, or the preference would reset on every load.
 */

export const SKPI_TABS = ['dashboard', 'people', 'targets', 'quality', 'definitions'] as const;
export type SkpiTab = typeof SKPI_TABS[number];

export const PEOPLE_VIEWS = ['ranking', 'matrix'] as const;
export type PeopleView = typeof PEOPLE_VIEWS[number];

/** Leaderboard GROUP_BY, mirrored so this file needs no component import. */
export const PEOPLE_GROUPS = ['none', 'team', 'company'] as const;
export type PeopleGroup = typeof PEOPLE_GROUPS[number];

/** DrillDownTable CellMode, mirrored. */
export const MATRIX_CELLS = ['actual', 'achievement', 'target', 'prior'] as const;
export type MatrixCell = typeof MATRIX_CELLS[number];

/** DrillDownTable Hierarchy, mirrored. */
export const MATRIX_HIERARCHIES = ['company-team', 'team'] as const;
export type MatrixHierarchy = typeof MATRIX_HIERARCHIES[number];

/** The target levels of the correction grid (E5). */
export const TARGET_LEVELS = ['person', 'team', 'company', 'bu'] as const;
export type TargetLevel = typeof TARGET_LEVELS[number];

/** The KPI families (strategicKpiFormat CATEGORY_ORDER), mirrored. */
export const SKPI_FAMILIES = ['crm', 'visits', 'gp', 'bab', 'marketdata'] as const;
export type SkpiFamily = typeof SKPI_FAMILIES[number];

/** The scope types of an evidence node (types/strategicKpis EvidenceScopeType), mirrored. */
export const DRILL_SCOPES = ['PERSON', 'TEAM', 'COMPANY', 'BU', 'TOTAL', 'SELECTION'] as const;
export type DrillScope = typeof DRILL_SCOPES[number];

export interface DrillNode {
    kpiKey: string;
    scopeType: DrillScope;
    scopeKey: string;
}

export interface SkpiUrlState {
    tab: SkpiTab;
    view: PeopleView;
    /** Ranking KPI; null = the component's own first choice. */
    kpi: string | null;
    group: PeopleGroup;
    /** The one family (People matrix, Targets grid); null = the component's default. */
    fam: SkpiFamily | null;
    cell: MatrixCell;
    hier: MatrixHierarchy;
    level: TargetLevel;
    /** Set while the drill view replaces the tab content. */
    drill: DrillNode | null;
}

/** The People part of the state: what survives a tab change (E22). */
export type PeopleState = Pick<SkpiUrlState, 'view' | 'kpi' | 'group' | 'fam' | 'cell' | 'hier'>;

/** What the page knows beyond the address (both optional; without them the functions behave as in A2a). */
export interface SkpiUrlOptions {
    /** The last view chosen (localStorage `strategic-kpis-drill-view`); fills a People address without `view`. */
    storedView?: PeopleView | null;
    /** The People state the page left; `withTab` restores it when People is entered from another tab. */
    people?: PeopleState | null;
}

export const SKPI_URL_DEFAULTS: Readonly<SkpiUrlState> = Object.freeze({
    tab: 'dashboard',
    view: 'ranking',
    kpi: null,
    group: 'none',
    fam: null,
    cell: 'achievement',
    hier: 'company-team',
    level: 'person',
    drill: null,
});

/** Every key this file reads or writes (the selection keys are not among them). */
export const SKPI_URL_KEYS = ['tab', 'view', 'kpi', 'group', 'fam', 'cell', 'hier', 'level', 'rows', 'dkpi', 'dscope', 'dkey'] as const;

const DRILL_KEYS = ['rows', 'dkpi', 'dscope', 'dkey'] as const;

function oneOf<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
    return value !== null && (allowed as readonly string[]).includes(value) ? value as T : fallback;
}

function nonEmpty(value: string | null): string | null {
    return value !== null && value.trim() !== '' ? value : null;
}

/** The old `view` values of the dashboard drill-down (before the redesign). */
const LEGACY_VIEW: Record<string, PeopleView> = { leaderboard: 'ranking', matrix: 'matrix' };

/** Params the old page wrote only for its dashboard drill-down: on an old link they mean People. */
const LEGACY_RANKING_KEYS = ['kpi', 'group'] as const;
const LEGACY_MATRIX_KEYS = ['cell', 'hier'] as const;

/**
 * Reads the address. Unknown or invalid values fall back to their default; old links land on the tab
 * that now holds them (rule 11). A tab's own params count only on that tab, which keeps `view` without
 * `tab=people` unambiguous: it can only be an old dashboard link. The drill node is read only with
 * `rows=1` and all three node params.
 */
export function parseSkpiUrl(params: URLSearchParams, opts: SkpiUrlOptions = {}): SkpiUrlState {
    const rawTab = params.get('tab');
    const rawView = params.get('view');
    let tab = oneOf<SkpiTab>(rawTab, SKPI_TABS, 'dashboard');
    // A People address without a (valid) view opens on the stored one (E22); old links set theirs below.
    let view: PeopleView = rawView === 'leaderboard' ? 'ranking' : oneOf<PeopleView>(rawView, PEOPLE_VIEWS, viewDefault(opts));

    // Before the redesign the drill-down sat on the dashboard (no tab param, or tab=dashboard).
    if (tab === 'dashboard') {
        const has = (keys: readonly string[]) => keys.some(k => params.get(k) !== null);
        if (rawView !== null && rawView in LEGACY_VIEW) { tab = 'people'; view = LEGACY_VIEW[rawView]; }
        else if (has(LEGACY_MATRIX_KEYS)) { tab = 'people'; view = 'matrix'; }
        else if (has(LEGACY_RANKING_KEYS)) { tab = 'people'; view = 'ranking'; }
    }

    const people = tab === 'people';
    const targets = tab === 'targets';
    return {
        tab,
        view: people ? view : SKPI_URL_DEFAULTS.view,
        kpi: people ? nonEmpty(params.get('kpi')) : null,
        group: people ? oneOf<PeopleGroup>(params.get('group'), PEOPLE_GROUPS, SKPI_URL_DEFAULTS.group) : SKPI_URL_DEFAULTS.group,
        fam: people || targets ? (oneOf<SkpiFamily | ''>(params.get('fam'), SKPI_FAMILIES, '') || null) : null,
        cell: people ? oneOf<MatrixCell>(params.get('cell'), MATRIX_CELLS, SKPI_URL_DEFAULTS.cell) : SKPI_URL_DEFAULTS.cell,
        hier: people ? oneOf<MatrixHierarchy>(params.get('hier'), MATRIX_HIERARCHIES, SKPI_URL_DEFAULTS.hier) : SKPI_URL_DEFAULTS.hier,
        level: targets ? oneOf<TargetLevel>(params.get('level'), TARGET_LEVELS, SKPI_URL_DEFAULTS.level) : SKPI_URL_DEFAULTS.level,
        drill: parseDrill(params),
    };
}

function parseDrill(params: URLSearchParams): DrillNode | null {
    if (params.get('rows') !== '1') return null;
    const kpiKey = nonEmpty(params.get('dkpi'));
    const scopeType = params.get('dscope');
    const scopeKey = params.get('dkey');
    if (kpiKey === null || scopeKey === null || !DRILL_SCOPES.includes(scopeType as DrillScope)) return null;
    return { kpiKey, scopeType: scopeType as DrillScope, scopeKey };
}

/** The view a People address without `view` means: the stored one, else ranking. */
function viewDefault(opts: SkpiUrlOptions): PeopleView {
    return opts.storedView ?? SKPI_URL_DEFAULTS.view;
}

/**
 * Writes the state into a copy of `prev`: keys not owned here (bu, year, the selection lists, anything
 * else) are kept; an owned key is set when it differs from its default and belongs to the state's tab
 * (`fam`: People and Targets), and deleted otherwise. `view` is left out only when it is ranking and
 * ranking is also what an address without it means for this person (no stored matrix), so an explicit
 * view never turns into another one on the next read.
 */
export function serializeSkpiUrl(state: SkpiUrlState, prev: URLSearchParams = new URLSearchParams(), opts: SkpiUrlOptions = {}): URLSearchParams {
    const next = new URLSearchParams(prev);
    for (const k of SKPI_URL_KEYS) next.delete(k);
    const put = (key: string, value: string | null, dflt: string | null) => { if (value !== null && value !== dflt) next.set(key, value); };
    put('tab', state.tab, SKPI_URL_DEFAULTS.tab);
    // A tab's own params are written only on that tab (see parseSkpiUrl).
    if (state.tab === 'people') {
        if (state.view !== SKPI_URL_DEFAULTS.view || viewDefault(opts) !== SKPI_URL_DEFAULTS.view) next.set('view', state.view);
        put('kpi', state.kpi, null);
        put('group', state.group, SKPI_URL_DEFAULTS.group);
        put('cell', state.cell, SKPI_URL_DEFAULTS.cell);
        put('hier', state.hier, SKPI_URL_DEFAULTS.hier);
    }
    if (state.tab === 'people' || state.tab === 'targets') put('fam', state.fam, null);
    if (state.tab === 'targets') put('level', state.level, SKPI_URL_DEFAULTS.level);
    if (state.drill) {
        next.set('rows', '1');
        next.set('dkpi', state.drill.kpiKey);
        next.set('dscope', state.drill.scopeType);
        next.set('dkey', state.drill.scopeKey);
    }
    return next;
}

/** Changes a few fields of the state read from `prev` and writes it back (for `setParams(…, { replace: true })`). */
export function withSkpiState(prev: URLSearchParams, patch: Partial<SkpiUrlState>, opts: SkpiUrlOptions = {}): URLSearchParams {
    return serializeSkpiUrl({ ...parseSkpiUrl(prev, opts), ...patch }, prev, opts);
}

/** The People fields of a state, for the page to remember when it leaves People (E22). */
export function peopleStateOf(state: SkpiUrlState): PeopleState {
    return { view: state.view, kpi: state.kpi, group: state.group, fam: state.fam, cell: state.cell, hier: state.hier };
}

/**
 * Another tab: the drill view closes, and the params of the tab left behind leave the address (`fam`
 * carries over between People and Targets). Entering People from another tab restores `opts.people`,
 * the state the page remembered (E22; a `fam` the Targets tab set wins); without one, People opens on
 * the stored view.
 */
export function withTab(prev: URLSearchParams, tab: SkpiTab, opts: SkpiUrlOptions = {}): URLSearchParams {
    const cur = parseSkpiUrl(prev, opts);
    let patch: Partial<SkpiUrlState> = { tab, drill: null };
    if (tab === 'people' && cur.tab !== 'people') {
        patch = opts.people
            ? { ...patch, ...opts.people, fam: cur.fam ?? opts.people.fam }
            : { ...patch, view: viewDefault(opts) };
    }
    return serializeSkpiUrl({ ...cur, ...patch }, prev, opts);
}

/** Opens (node) or leaves (null) the drill view. Opening is the one change the page writes with push, not replace. */
export function withDrill(prev: URLSearchParams, node: DrillNode | null, opts: SkpiUrlOptions = {}): URLSearchParams {
    return withSkpiState(prev, { drill: node }, opts);
}

/**
 * The canonical form of an address, or null when it already is canonical. A2b replaces the address
 * once with it on load, so an old `?view=leaderboard` reads `?tab=people` afterwards and invalid
 * values disappear. Keys not owned here keep their order and value.
 */
export function normalizeSkpiParams(params: URLSearchParams, opts: SkpiUrlOptions = {}): URLSearchParams | null {
    const next = serializeSkpiUrl(parseSkpiUrl(params, opts), params, opts);
    return sameParams(params, next) ? null : next;
}

function sameParams(a: URLSearchParams, b: URLSearchParams): boolean {
    const sa = [...a.entries()].map(([k, v]) => `${k}=${v}`).sort();
    const sb = [...b.entries()].map(([k, v]) => `${k}=${v}`).sort();
    return sa.length === sb.length && sa.every((e, i) => e === sb[i]);
}

/** True when a key belongs to the drill view (handy for a Back test or a crumb). */
export function isDrillKey(key: string): boolean {
    return (DRILL_KEYS as readonly string[]).includes(key);
}

// ------------------------------------------------------------------ browser storage

/** The phase whose package removes the owner of a key; null = the owner stays after the redesign. */
export type SkpiPhase = 'A' | 'B' | 'C' | 'D';

export interface SkpiStorageKey {
    key: string;
    /** Who wrote it before the redesign (file and what it remembers). */
    owner: string;
    retiredIn: SkpiPhase | null;
}

/**
 * Inventory of every `strategic-kpis-*` localStorage key (grep over web/src, 2026-10-19). The test holds
 * it complete against the source: a new key must be added here with its retirement phase.
 */
export const SKPI_STORAGE_KEYS: readonly SkpiStorageKey[] = Object.freeze([
    { key: 'strategic-kpis-drill-view', owner: 'StrategicKpisPage: leaderboard|matrix preference; stays (E22): fills a People address without view', retiredIn: null },
    { key: 'strategic-kpis-columns', owner: 'StrategicKpisPage: matrix column pills incl. «Steered»; one family instead (E18, B3b)', retiredIn: 'B' },
    { key: 'strategic-kpis-evidence-col-widths', owner: 'EvidenceModal: column widths; the modal gives way to the evidence panel (E9, B2)', retiredIn: 'B' },
    { key: 'strategic-kpis-targets-columns', owner: 'TargetsTab: column pills; one family instead (E5, C3b)', retiredIn: 'C' },
    { key: 'strategic-kpis-targets-person-col-widths', owner: 'TargetsTab person grid: column widths; TargetGrid replaces it (C3a)', retiredIn: 'C' },
    { key: 'strategic-kpis-targets-group-col-widths', owner: 'TargetsTab group grid: column widths; TargetGrid replaces it (C3a)', retiredIn: 'C' },
    { key: 'strategic-kpis-audit-col-widths', owner: 'TargetAuditTable: column widths; the audit moves to a panel (C4)', retiredIn: 'C' },
    { key: 'strategic-kpis-leaderboard-col-widths', owner: 'Leaderboard: column widths; the ranking stays on People', retiredIn: null },
    { key: 'strategic-kpis-drilldown-col-widths', owner: 'DrillDownTable: column widths; the matrix stays on People', retiredIn: null },
    { key: 'strategic-kpis-hide-no-targets', owner: 'StrategicKpisPage: matrix «Hide persons without targets»', retiredIn: null },
    { key: 'strategic-kpis-context-open', owner: 'ContextKpis: the collapsible strip open or closed (Q9)', retiredIn: null },
]);

const PHASE_ORDER: readonly SkpiPhase[] = ['A', 'B', 'C', 'D'];

/** The keys retired up to and including `phase` (cumulative: B also deletes what A retired). */
export function retiredSkpiStorageKeys(phase: SkpiPhase): string[] {
    const upTo = PHASE_ORDER.indexOf(phase);
    return SKPI_STORAGE_KEYS
        .filter(k => k.retiredIn !== null && PHASE_ORDER.indexOf(k.retiredIn) <= upTo)
        .map(k => k.key);
}

/** The part of `Storage` this needs, so a test can pass a plain object. */
export interface StorageLike {
    getItem(key: string): string | null;
    removeItem(key: string): void;
}

/**
 * Deletes the retired keys that are present and returns which ones it deleted. A storage that throws
 * (private mode, blocked site data) deletes nothing and returns [].
 */
export function clearRetiredSkpiStorage(phase: SkpiPhase, storage?: StorageLike): string[] {
    try {
        const s = storage ?? window.localStorage;
        const removed: string[] = [];
        for (const key of retiredSkpiStorageKeys(phase)) {
            if (s.getItem(key) !== null) { s.removeItem(key); removed.push(key); }
        }
        return removed;
    } catch {
        return [];
    }
}

// ------------------------------------------------------------------ the stored People view (E22)

const VIEW_STORAGE_KEY = 'strategic-kpis-drill-view';

/** `Storage` with writing, for the view preference. */
export interface WritableStorageLike extends StorageLike {
    setItem(key: string, value: string): void;
}

/**
 * The last chosen People view, or null. The old page wrote `leaderboard|matrix`; both spellings are
 * read. A storage that throws reads null.
 */
export function readStoredPeopleView(storage?: StorageLike): PeopleView | null {
    try {
        const raw = (storage ?? window.localStorage).getItem(VIEW_STORAGE_KEY);
        if (raw === 'leaderboard' || raw === 'ranking') return 'ranking';
        if (raw === 'matrix') return 'matrix';
        return null;
    } catch {
        return null;
    }
}

/** Remembers the chosen view in the old spelling (`leaderboard|matrix`), which the pre-redesign bundle also reads. */
export function storePeopleView(view: PeopleView, storage?: WritableStorageLike): void {
    try {
        (storage ?? window.localStorage).setItem(VIEW_STORAGE_KEY, view === 'ranking' ? 'leaderboard' : 'matrix');
    } catch { /* blocked site data: the preference is not kept */ }
}
